import React from "react";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "../../../hooks";
import { useInstitutionTransmissions } from "../../../hooks/useInstitutionTransmissions";
import {
  InstitutionListItem,
  InstitutionWithDatasets,
} from "../../../services/index";
import LoadingSpinner from "../../common/LoadingSpinner";
import Card from "../../ui/Card";
import Icon from "../../ui/Icon";

const InstitutionConnectionManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { navigateToMyDataManagement, navigateToTransmissionDemandDetail } =
    useNavigation();
  const { institutions, loading } = useInstitutionTransmissions();

  // 공공기관과 의료기관 분류
  const publicInstitutions = institutions.filter(
    (inst) => inst.institutionType === "public"
  );
  const medicalInstitutions = institutions.filter(
    (inst) => inst.institutionType === "hospital"
  );

  const handleInstitutionClick = (institution: InstitutionListItem) => {
    if (institution.hasTransmissionDemand && institution.transmissionDemandId) {
      // 전송요구서가 연결되어 있는 경우: 상세 페이지로 이동
      navigateToTransmissionDemandDetail(institution.transmissionDemandId);
    } else {
      // 연결 가능한 경우: MyDataAgreementPage로 이동
      const selectedInstitution: InstitutionWithDatasets = {
        provideInstitutionNo: institution.provideInstitutionNo,
        provideInstitutionName: institution.institutionName,
        datasets: institution.datasets,
        type: institution.institutionType,
      };

      navigate("/settings/institution-connection/new", {
        state: { selectedInstitutions: [selectedInstitution] },
      });
    }
  };

  const getStatusBadge = (institution: InstitutionListItem) => {
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${
          institution.hasTransmissionDemand
            ? "bg-blue-100 text-blue-600"
            : "bg-gray-100 text-gray-600"
        }`}
      >
        {institution.hasTransmissionDemand ? "연결 중" : "연결 가능"}
      </span>
    );
  };

  const renderInstitutionCard = (institution: InstitutionListItem) => (
    <Card
      key={institution.provideInstitutionNo}
      className="cursor-pointer hover:bg-gray-50 transition-colors"
    >
      <button
        onClick={() => handleInstitutionClick(institution)}
        className="w-full text-left"
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-medium text-gray-800 flex-1">
            {institution.institutionName}
          </h3>
          {getStatusBadge(institution)}
        </div>
        {institution.institutionType === "public" &&
          institution.datasets &&
          institution.datasets.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              {institution.datasets
                .map((dataset) => dataset.datasetName)
                .join(", ")}
            </p>
          )}
      </button>
    </Card>
  );

  return (
    <div className="h-screen-dvh bg-white flex flex-col">
      {/* 상단 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={navigateToMyDataManagement}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>      </header>

      {/* 메인 콘텐츠 */}
      <main className="flex-1 p-4 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="space-y-6">
            {/* 공공기관 섹션 */}
            {publicInstitutions.length > 0 && (
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  공공기관
                </h2>
                <div className="space-y-3">
                  {publicInstitutions.map(renderInstitutionCard)}
                </div>
              </section>
            )}

            {/* 의료기관 섹션 */}
            {medicalInstitutions.length > 0 && (
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">
                  의료기관
                </h2>
                <div className="space-y-3">
                  {medicalInstitutions.map(renderInstitutionCard)}
                </div>
              </section>
            )}

            {institutions.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">연결된 기관이 없습니다.</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default InstitutionConnectionManagementPage;
